'use client'

import { InputSelect, InputText } from '@vinted/web-ui'
import classNames from 'classnames'
import { noop } from 'lodash'
import { ChangeEvent, FocusEvent, ReactNode, useEffect, useMemo, useState } from 'react'

import { isNativeDatePickerSupported } from './utils'

type Props = {
  name: string
  validation?: ReactNode
  title?: string | JSX.Element
  dayPlaceholder: string
  monthPlaceholder: string
  yearPlaceholder: string
  value?: string | null
  disabled?: boolean
  styling?: React.ComponentProps<typeof InputText>['styling']
  theme?: React.ComponentProps<typeof InputSelect>['theme']
  disableHtmlValidations?: boolean
  autoComplete?: string
  onChange?: (date: string | null) => void
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void
  isPhone: boolean
  isoLocale: string
}

type DateSelection = {
  year?: string
  month?: string
  day?: string
}

const toDate = (dateSelection: DateSelection) => {
  const year = Number(dateSelection.year)
  const month = Number(dateSelection.month)
  const day = Number(dateSelection.day)
  const date = new Date(year, month, day)

  if (Number.isNaN(date.getTime())) return undefined

  const shifted = date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day

  if (shifted) return undefined

  return date
}

const isDateValid = (date: Date) => !Number.isNaN(date.getTime())

const formatDateForBackend = (date: Date) => {
  return [
    date.getFullYear(),
    `0${date.getMonth() + 1}`.slice(-2),
    `0${date.getDate()}`.slice(-2),
  ].join('-')
}

const InputBirthdate = ({
  name,
  validation,
  title,
  dayPlaceholder,
  monthPlaceholder,
  yearPlaceholder,
  disabled,
  styling,
  theme,
  value,
  disableHtmlValidations,
  autoComplete,
  onChange = noop,
  onBlur = noop,
  onFocus = noop,
  isPhone,
  isoLocale,
}: Props) => {
  const [dateSelection, setDateSelection] = useState<DateSelection>({})

  useEffect(() => {
    if (!value) return

    const date = new Date(value)

    if (!isDateValid(date)) return

    setDateSelection({
      year: date.getFullYear().toString(),
      month: date.getMonth().toString(),
      day: date.getDate().toString(),
    })
  }, [value])

  function handleDateFieldChange(field: string) {
    return function handleDateChange(event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) {
      const newDateSelection = {
        ...dateSelection,
        [field]: event.target.value,
      }

      const newDate = toDate(newDateSelection)

      if (newDate) {
        onChange(formatDateForBackend(newDate))
      } else {
        onChange(null)
      }

      setDateSelection(newDateSelection)
    }
  }

  function handleNativeDateChange(event: ChangeEvent<HTMLInputElement>) {
    const { value: inputValue } = event.target

    onChange(inputValue)

    if (inputValue) {
      const date = new Date(inputValue)
      setDateSelection({
        year: date.getFullYear().toString(),
        month: date.getMonth().toString(),
        day: date.getDay.toString(),
      })
    } else {
      setDateSelection({ year: undefined, month: undefined, day: undefined })
    }
  }

  const monthOptions = useMemo(() => {
    const monthFormatter = new Intl.DateTimeFormat(isoLocale, { month: 'long' })

    return Array.from(Array(12).keys()).map(month => ({
      value: month.toString(),
      label: monthFormatter.format(Date.UTC(2000, month, 1, 0, 0, 0)),
    }))
  }, [isoLocale])

  const inputClass = classNames(
    'c-input',
    { [`c-input--${String(styling)}`]: styling !== undefined },
    { [`c-input--${String(theme)}`]: theme !== undefined },
  )

  const shouldRenderNative = useMemo(() => isNativeDatePickerSupported() && isPhone, [isPhone])

  if (shouldRenderNative) {
    return (
      <InputText
        name="birthdate"
        type="date"
        title={title}
        styling={styling}
        disabled={disabled}
        value={value}
        validation={validation}
        onChange={handleNativeDateChange}
        onBlur={onBlur}
        onFocus={onFocus}
        testId="birthdate-native-input"
      />
    )
  }

  return (
    <label htmlFor={name} className={inputClass}>
      {title && <div className="c-input__title">{title}</div>}
      <div className="c-input__content input-birthdate__content">
        <InputText
          placeholder={dayPlaceholder}
          name="day"
          type={disableHtmlValidations ? undefined : 'number'}
          styling="tight"
          disabled={disabled}
          value={dateSelection.day ?? ''}
          onChange={handleDateFieldChange('day')}
          onBlur={onBlur}
          onFocus={onFocus}
          testId="day-input"
          autoComplete={autoComplete}
        />
        <InputSelect
          name="month"
          styling="tight"
          theme={theme}
          required={!disableHtmlValidations}
          disabled={disabled}
          placeholder={monthPlaceholder}
          value={dateSelection.month ?? ''}
          onChange={handleDateFieldChange('month')}
          options={monthOptions}
          testId="month-input"
        />
        <InputText
          placeholder={yearPlaceholder}
          name="year"
          type={disableHtmlValidations ? undefined : 'number'}
          styling="tight"
          disabled={disabled}
          value={dateSelection.year ?? ''}
          onChange={handleDateFieldChange('year')}
          onBlur={onBlur}
          onFocus={onFocus}
          testId="year-input"
          autoComplete={autoComplete}
        />
      </div>
      {validation && <div className="c-input__note">{validation}</div>}
    </label>
  )
}

export default InputBirthdate
